import React, { useState, useEffect } from 'react';
import { auditAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import { FileText, Eye, X, RefreshCw } from 'lucide-react';

export default function AuditLogsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterTable, setFilterTable] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await auditAPI.getAll();
      setData(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load audit logs');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Get unique tables and users for filters
  const tables = Array.from(new Set(data.map(d => d.tableName).filter(Boolean))).sort();
  const users = Array.from(new Set(data.map(d => d.username).filter(Boolean))).sort();

  // Filter data
  const filteredData = data.filter(item => {
    if (filterAction && item.action !== filterAction) return false;
    if (filterTable && item.tableName !== filterTable) return false;
    if (filterUser && item.username !== filterUser) return false;
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      if (new Date(item.logTime) < from) return false;
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      if (new Date(item.logTime) > to) return false;
    }
    return true;
  });

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'INSERT': return 'badge-green';
      case 'UPDATE': return 'badge-blue';
      case 'DELETE': return 'badge-red';
      default: return 'badge-navy';
    }
  };

  const parseJson = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  const getDiffs = (oldStr, newStr) => {
    const oldObj = parseJson(oldStr) || {};
    const newObj = parseJson(newStr) || {};
    const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)])).sort();
    
    const diffs = [];
    allKeys.forEach(k => {
      // Ignore internal passwords/sensitive hashes
      if (k.toLowerCase().includes('password')) return;
      
      const oldVal = oldObj[k];
      const newVal = newObj[k];
      
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diffs.push({
          key: k,
          oldValue: oldVal === undefined || oldVal === null ? '—' : String(oldVal),
          newValue: newVal === undefined || newVal === null ? '—' : String(newVal)
        });
      }
    });
    return diffs;
  };

  const renderDiffModalContent = () => {
    if (!selectedLog) return null;
    const oldObj = parseJson(selectedLog.oldValues);
    const newObj = parseJson(selectedLog.newValues);

    const logHeader = (
      <div style={{ background: 'var(--silver-100)', border: '1px solid var(--silver-200)', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 12px', fontSize: 13 }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>User:</span>
          <span style={{ fontWeight: 600, color: 'var(--navy-800)' }}>{selectedLog.username || 'System'}</span>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>IP Address:</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--navy-700)' }}>{selectedLog.iPAddress || selectedLog.ipAddress || '—'}</span>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Activity:</span>
          <span>
            User <strong style={{ color: 'var(--navy-800)' }}>{selectedLog.username || 'System'}</strong> performed <span className={`badge ${getActionBadgeClass(selectedLog.action)}`} style={{ textTransform: 'uppercase', verticalAlign: 'middle', marginRight: 4, padding: '2px 6px', fontSize: 10 }}>{selectedLog.action}</span> on table <strong style={{ color: 'var(--navy-800)' }}>{selectedLog.tableName}</strong> (ID: {selectedLog.recordID || 'N/A'}).
          </span>
        </div>
      </div>
    );

    if (selectedLog.action === 'INSERT') {
      const fields = newObj ? Object.entries(newObj) : [];
      return (
        <div>
          {logHeader}
          <div style={{ marginBottom: 12 }}><span className="badge badge-green">INSERT</span> <strong>{selectedLog.tableName}</strong> (ID: {selectedLog.recordID})</div>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>All values inserted:</p>
          <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="table borderless compact">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {fields.filter(([k]) => !k.toLowerCase().includes('password')).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ fontWeight: 600 }}>{k}</td>
                    <td><span style={{ color: 'var(--green-700)', background: 'rgba(16,185,129,0.1)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>{v === null ? 'null' : String(v)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (selectedLog.action === 'DELETE') {
      const fields = oldObj ? Object.entries(oldObj) : [];
      return (
        <div>
          {logHeader}
          <div style={{ marginBottom: 12 }}><span className="badge badge-red">DELETE</span> <strong>{selectedLog.tableName}</strong> (ID: {selectedLog.recordID})</div>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>Values before deletion:</p>
          <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="table borderless compact">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {fields.filter(([k]) => !k.toLowerCase().includes('password')).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ fontWeight: 600 }}>{k}</td>
                    <td><span style={{ color: 'var(--red-700)', background: 'rgba(239,68,68,0.1)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12, textDecoration: 'line-through' }}>{v === null ? 'null' : String(v)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // UPDATE Diff
    const diffs = getDiffs(selectedLog.oldValues, selectedLog.newValues);

    return (
      <div>
        {logHeader}
        <div style={{ marginBottom: 12 }}><span className="badge badge-blue">UPDATE</span> <strong>{selectedLog.tableName}</strong> (ID: {selectedLog.recordID})</div>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>Only changed fields are shown:</p>
        {diffs.length === 0 ? (
          <div className="text-center text-muted py-4">No fields modified or password hash updated.</div>
        ) : (
          <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="table compact" style={{ border: '1px solid var(--border)', width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--silver-100)' }}>
                  <th style={{ width: '30%', padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Field</th>
                  <th style={{ width: '35%', padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Old Value</th>
                  <th style={{ width: '35%', padding: '8px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>New Value</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map(d => (
                  <tr key={d.key}>
                    <td style={{ fontWeight: 600, padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{d.key}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--red-700)', background: 'rgba(239,68,68,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>
                        {d.oldValue}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--green-700)', background: 'rgba(16,185,129,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>
                        {d.newValue}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const openDetail = async (row) => {
    try {
      const res = await auditAPI.getById(row.auditID);
      setSelectedLog(res.data?.data || row);
      setModalOpen(true);
    } catch {
      setSelectedLog(row);
      setModalOpen(true);
    }
  };

  const columns = [
    { header: 'Time', key: 'logTime', render: r => <span className="text-mono" style={{ fontSize: 12 }}>{new Date(r.logTime).toLocaleString('en-IN')}</span> },
    { header: 'User', key: 'username', render: r => <span style={{ fontWeight: 600 }}>{r.username}</span> },
    { header: 'IP Address', key: 'iPAddress', render: r => <span className="text-mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.iPAddress || r.ipAddress || '—'}</span> },
    { header: 'Action', key: 'action', render: r => <span className={`badge ${getActionBadgeClass(r.action)}`}>{r.action}</span> },
    { header: 'Table', key: 'tableName', render: r => <code style={{ fontSize: 12 }}>{r.tableName}</code> },
    { header: 'Record ID', key: 'recordID', render: r => <span className="badge badge-navy">{r.recordID || 'N/A'}</span> },
    {
      header: 'Details', align: 'center', render: r => (
        <button 
          className="btn btn-ghost btn-sm btn-icon" 
          onClick={() => openDetail(r)}
          title="View Details"
        >
          <Eye size={15} />
        </button>
      )
    }
  ];

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Audit Logs</h1>
          <p className="text-muted">Database activity logs and details changes tracking</p>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={load} disabled={loading} title="Reload logs">
          <RefreshCw size={16} className={loading ? 'spinner' : ''} />
        </button>
      </div>

      {/* Filter panel */}
      <div className="card mb-6" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 12 }}>Action</label>
            <select className="form-control" value={filterAction} onChange={e => setFilterAction(e.target.value)}>
              <option value="">All Actions</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 12 }}>Table Name</label>
            <select className="form-control" value={filterTable} onChange={e => setFilterTable(e.target.value)}>
              <option value="">All Tables</option>
              {tables.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 12 }}>User</label>
            <select className="form-control" value={filterUser} onChange={e => setFilterUser(e.target.value)}>
              <option value="">All Users</option>
              {users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 12 }}>From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 12 }}>To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={e => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          <DataTable 
            data={filteredData} 
            columns={columns} 
            loading={loading} 
            searchKeys={['tableName', 'username', 'action', 'recordID', 'iPAddress', 'ipAddress']} 
            emptyIcon={<FileText size={36} />} 
            emptyText="No audit logs found" 
          />
        </div>
      </div>

      {modalOpen && selectedLog && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && (setModalOpen(false), setSelectedLog(null))}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>Audit Detail — Log #{selectedLog.auditID}</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => { setModalOpen(false); setSelectedLog(null); }}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              {renderDiffModalContent()}
            </div>
            <div className="modal-footer">
              <span className="text-muted text-mono" style={{ fontSize: 11, marginRight: 'auto' }}>
                Logged at: {new Date(selectedLog.logTime).toLocaleString('en-IN')}
              </span>
              <button type="button" className="btn btn-ghost" onClick={() => { setModalOpen(false); setSelectedLog(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
